import React, { useState } from 'react';
import Modal from './Modal';
import Icon from './icons';
import { apiPost } from '../lib/api';
import { canManage } from '../lib/roles';

// Lets an admin add many skills at once instead of one API call per skill,
// and — via "Generate from persona" — without writing any JSON at all.
// Generated/pasted skills land as lifecycle=draft / review=unscanned (see
// registry.js POST /skills/bulk, build jul22-billing) so they go through Security
// review queue before going live.
export default function BulkImportSkills({ onImported }) {
  const [open, setOpen] = useState(false);
  const [persona, setPersona] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [sourceNotes, setSourceNotes] = useState(null);

  if (!canManage()) return null;

  function reset() {
    setPersona('');
    setText('');
    setError('');
    setResult(null);
    setSourceNotes(null);
  }

  function close() {
    setOpen(false);
    reset();
  }

  async function generateFromPersona() {
    const personas = persona.split(',').map((s) => s.trim()).filter(Boolean);
    if (personas.length === 0) return;
    setError('');
    setResult(null);
    setGenerating(true);
    try {
      const res = await apiPost('/skills/drafts/from-persona', { personas });
      setText(JSON.stringify(res.skills, null, 2));
      setSourceNotes(res.source_notes || null);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setGenerating(false);
    }
  }

  async function submit() {
    setError('');
    setResult(null);
    let skills;
    try {
      const parsed = JSON.parse(text);
      skills = Array.isArray(parsed) ? parsed : parsed.skills;
      if (!Array.isArray(skills) || skills.length === 0) {
        throw new Error('Expected a JSON array of skills, or { "skills": [...] }.');
      }
    } catch (e) {
      setError(`Invalid JSON: ${e.message}`);
      return;
    }

    setBusy(true);
    try {
      const res = await apiPost('/skills/bulk', { skills });
      setResult(res);
      if (res.created_count > 0 && onImported) onImported();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" className="btn-secondary" onClick={() => setOpen(true)}>
        <Icon name="upload" /> Bulk Import
      </button>
      {open && (
        <Modal
          title="Bulk import skills"
          onClose={close}
          wide
          footer={
            <>
              <button type="button" className="btn-primary" onClick={submit} disabled={busy || !text.trim()}>
                {busy ? 'Importing…' : 'Import'}
              </button>
            </>
          }
        >
          <p className="bulk-import__hint">
            Type a persona/department name — no task list needed, it's filled in automatically
            (built-in library, or Claude if <code>ANTHROPIC_API_KEY</code> is set and the persona
            is unrecognized). Or paste your own JSON below. Imported skills are created as{' '}
            <strong>draft / unscanned</strong> — review and approve them on the Security page
            before they go live.
          </p>
          <div className="search-bar--inline" style={{ marginBottom: '0.9rem' }}>
            <input
              className="field-input"
              style={{ width: '100%', margin: 0 }}
              placeholder="Persona(s), e.g. Legal, HR, Finance"
              value={persona}
              onChange={(e) => setPersona(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') generateFromPersona(); }}
            />
            <button
              type="button"
              className="btn-secondary btn-small"
              onClick={generateFromPersona}
              disabled={generating || !persona.trim()}
            >
              {generating ? 'Generating…' : 'Generate'}
            </button>
          </div>
          {sourceNotes && (
            <p className="bulk-import__hint" style={{ marginTop: '-0.4rem' }}>
              {sourceNotes.map((n) => `${n.department}: ${n.task_count} tasks (${n.source})`).join(' · ')}
            </p>
          )}
          <textarea
            className="bulk-import__textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Generated skills will appear here — or paste your own JSON array."
          />
          {error && <div className="status status--error" style={{ marginTop: '0.75rem' }}>{error}</div>}
          {result && (
            <div className="bulk-import__summary">
              Created <strong>{result.created_count}</strong>, skipped{' '}
              <strong>{result.skipped_count}</strong> (already existed), errors{' '}
              <strong>{result.error_count}</strong>.
              {result.errors?.length > 0 && (
                <ul>
                  {result.errors.map((e, i) => (
                    <li key={i}>{e.key || '(no key)'}: {e.reason}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
